import React, { useContext } from 'react';
import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import { StyleSheet, css } from 'aphrodite/no-important';
import { ThemeContext } from '../theme';

const propTypes = {
  tabKey: PropTypes.string,
  parentKey: PropTypes.string,
  text: PropTypes.string,
  isActive: PropTypes.bool,
  style: PropTypes.object,
  iconClass: PropTypes.string,
  disableRename: PropTypes.bool,
  classNames: PropTypes.string,
  showCloseBtn: PropTypes.bool,
  showTabIcon: PropTypes.bool,
  showSvgIcon: PropTypes.bool,
  svgIcon: PropTypes.object,
  textStyles: PropTypes.object,
  showLoader: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  showBadge: PropTypes.bool,
  badgeColor: PropTypes.string,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
  onActivate: PropTypes.func
};

const defaultProps = {
  disableRename: false,
  showCloseBtn: false,
  showSvgIcon: false,
  textStyles: {},
  svgIcon: null,
  showBadge: false,
  showTabIcon: true,
  showLoader: false
};

const TabItem = (props) => {
  const { showCloseBtn, showTabIcon, showSvgIcon, showBadge } = props;
  const theme = useTheme();
  const context = useContext(ThemeContext);
  let tabs;
  if (theme && theme.tabs) {
    tabs = theme.tabs;
  } else {
    tabs = context.theme.tabs;
  }

  const {
    background,
    hoverBackground,
    activeBackground,
    indicatorBackground,
    indicatorHoverBackground,
    indicatorActiveBackground,
    textColor,
    hoverTextColor,
    activeTextColor,
    fontSize,
    fontWeight,
    tabPadding
  } = tabs;

  const styles = StyleSheet.create({
    tabItem: {
      position: 'relative',
      height: '40px',
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      cursor: 'pointer',
      marginRight: '16px',
      background: background,
      padding: tabPadding,
      color: textColor,
      fill: props.isActive ? activeTextColor : textColor,
      borderBottom: props.isActive ? `2px solid ${indicatorActiveBackground}` : `2px solid ${indicatorBackground}`,
      ':hover:not(.active)': {
        background: hoverBackground,
        color: hoverTextColor,
        borderBottom: `2px solid ${indicatorHoverBackground}`
      }
    },
    tabItemLoader: {
      position: 'absolute',
      right: 0
    },
    activeTabItem: {
      background: activeBackground
    },
    iconContainer: {
      width: '20px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '4px',
      padding: '0'
    },
    tabItemIcon: {
      fontSize: '20px',
      margin: '0',
      marginRight: '8px',
      color: props.isActive ? activeTextColor : textColor,
      fill: props.isActive ? activeTextColor : textColor,
      ':hover:not(.active)': {
        color: hoverTextColor
      }
    },
    tabItemText: {
      fontFamily: 'Lato',
      fontWeight: fontWeight,
      fontSize: fontSize,
      lineHeight: '14px',
      color: props.isActive ? activeTextColor : textColor
    },
    closeBtn: {
      height: '40px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    tabLoader: {
      position: 'absolute',
      right: '0',
      height: '40px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    closeBtnIcon: {
      color: textColor,
      ':hover': {
        color: hoverTextColor
      }
    },
    tabsBadge: {
      position: 'absolute',
      top: '0px',
      right: '4px',
      color: props.badgeColor
    }
  });

  return (
    <div
      key={props.parentKey}
      className={`${props && props.isActive ? 'active' : ''} ${css(styles.tabItem)} ${props.classNames}`}
      onClick={() => { props.onActivate(props.tabKey); }}
      style={{ ...props.style }}
    >
      {/* Tab Badge */}
      {showBadge
        ? <div className={css(styles.tabsBadge)}>
          <i className='fa fa-exclamation-circle' />
        </div>
        : null
      }

      {/* Tab Icon */}
      {showTabIcon && showSvgIcon
        ? <div className={css(styles.iconContainer)}>
          {props.svgIcon}
        </div>
        : null
      }

      {showTabIcon && !showSvgIcon
        ? <div className={css(styles.iconContainer)}>
          <i
            className={`${props.iconClass} ${css(styles.tabItemIcon)}`}
          />
        </div>
        : null
      }

      {/* Tab Text */}
      <div
        className={css(styles.tabItemText)}
        style={{ ...props.textStyles }}
      >
        {props.children ? props.children : null}
      </div>

      {/* Close Button */}
      {showCloseBtn
        ? <div
          className={css(styles.closeBtn)}
          onClick={() => {
            props.onDelete(props.parentKey, props.tabKey);
          }}
        >
          <i className={`fa fa-close ${css(styles.closeBtnIcon)}`} />
        </div>
        : null
      }
      {/* Loader */}
      {props.showLoader &&
        <div className={css(styles.tabLoader)}>
          <i className='fa fa-spinner fa-pulse fa-1x fa-fw' />
        </div>
      }
    </div>
  );
};

TabItem.propTypes = propTypes;
TabItem.defaultProps = defaultProps;

export { TabItem };
